import { createContext, useContext, useEffect, useRef, useState } from "react";
import axiosInstance from "./config/axios";

const SettingContext = createContext();

const STORAGE_KEY = "setting";

export const SettingProvider = ({ children }) => {
    const hasFetched = useRef(false);
    // Settings is consumed as an object everywhere (settingData.logo_header etc),
    // so it must default to an object rather than an array.
    const [settingData, setSettingData] = useState({});

    const fetchSettingData = async () => {
        try {
            const response = await axiosInstance.get("/frontend/settings");
            const data = response.data?.data?.[0];

            // An empty/malformed response must not overwrite good cached data,
            // and must never be written to storage - persisting `undefined`
            // stores the literal string "undefined", which then throws on every
            // subsequent page load when parsed back.
            if (!data || typeof data !== "object") {
                console.error("Settings response was empty or malformed", response?.data);
                return;
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            setSettingData(data);
        } catch (error) {
            console.error("Failed to fetch settings data", error);
        }
    };

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        // Use cached data immediately (fast UI). Corrupted cache must not break
        // the app - discard it and let the network fetch repopulate it.
        try {
            const storedData = localStorage.getItem(STORAGE_KEY);

            if (storedData) {
                const parsed = JSON.parse(storedData);

                if (parsed && typeof parsed === "object") {
                    setSettingData(parsed);
                } else {
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error("Discarding corrupted cached settings", error);
            localStorage.removeItem(STORAGE_KEY);
        }

        // Always update data on refresh
        fetchSettingData();
    }, []);

    return (
        <SettingContext.Provider value={{ settingData }}>
            {children}
        </SettingContext.Provider>
    );
};

// Custom Hook
export const useSetting = () => useContext(SettingContext);
